import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, ChevronUp, RotateCcw, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TableCell, TableRow } from '@/components/ui/table';
import type { DesignChange, UpdateDesignChangeReviewRequest } from '../../../types';

interface Props {
  row: DesignChange;
  isBusy?: boolean;
  onReview?: (request: UpdateDesignChangeReviewRequest) => Promise<void>;
}

const DesignChangeRow: React.FC<Props> = ({ row, isBusy = false, onReview }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const isRejected = row.reviewStatus === 'rejected';
  const isSuperseded = row.reviewStatus === 'superseded';

  return (
    <>
      <TableRow>
        <TableCell>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setOpen(!open)}
            aria-label={
              open
                ? t('drbfm.cadLink.designChangeCollapse', '設計変更詳細を閉じる')
                : t('drbfm.cadLink.designChangeExpand', '設計変更詳細を開く')
            }
          >
            {open ? <ChevronUp strokeWidth={1.9} /> : <ChevronDown strokeWidth={1.9} />}
          </Button>
        </TableCell>
        <TableCell>
          <div className="flex flex-wrap gap-1">
            <Badge variant="outline" className="revy-cjk border-border font-normal">
              {row.category}
            </Badge>
            {isRejected && (
              <Badge variant="destructive" className="revy-cjk font-normal">
                {t('drbfm.decisionMemory.rejectedStatus', '非採用')}
              </Badge>
            )}
            {isSuperseded && (
              <Badge variant="secondary" className="revy-cjk font-normal">
                {t('drbfm.decisionMemory.supersededStatus', '旧版')}
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="revy-cjk text-sm leading-[1.5]">{row.itemName}</TableCell>
        <TableCell>
          <p
            className="revy-cjk max-w-[300px] truncate text-sm leading-[1.5]"
            title={row.changeDescription || ''}
          >
            {row.changeDescription}
          </p>
        </TableCell>
        <TableCell>
          {row.impactAreas?.map((area, idx) => (
            <Badge key={idx} variant="secondary" className="revy-cjk mb-1 mr-1 font-normal">
              {area}
            </Badge>
          ))}
        </TableCell>
        {onReview && (
          <TableCell>
            <div className="flex justify-end gap-1">
              {isRejected ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isBusy}
                  onClick={() => void onReview({ reviewStatus: 'ai_candidate' })
                    .catch(() => undefined)}
                >
                  <RotateCcw />
                  {t('drbfm.decisionMemory.restoreForReview', '再検討に戻す')}
                </Button>
              ) : isSuperseded ? null : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isBusy}
                    onClick={() => void onReview({ reviewStatus: 'accepted' })
                      .catch(() => undefined)}
                  >
                    <Check />
                    {t('drbfm.decisionMemory.adopt', '採用')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    disabled={isBusy}
                    onClick={() => void onReview({ reviewStatus: 'rejected' })
                      .catch(() => undefined)}
                  >
                    <Trash2 />
                    {t('drbfm.decisionMemory.rejectCandidate', '非採用')}
                  </Button>
                </>
              )}
            </div>
          </TableCell>
        )}
      </TableRow>
      {open && (
        <TableRow className="hover:bg-transparent">
          <TableCell className="px-2 py-0" colSpan={onReview ? 6 : 5}>
            <div className="m-4">
              <p className="revy-cjk mb-2 text-sm font-medium leading-[1.5]">
                {t('drbfm.table.specifications')}
              </p>
              <div className="flex gap-4">
                <div className="flex-1 rounded-md border p-4">
                  <p className="revy-cjk text-xs leading-[1.5] text-muted-foreground">
                    {t('drbfm.table.oldSpec')}
                  </p>
                  <p className="revy-cjk whitespace-pre-wrap text-sm leading-[1.6]">
                    {row.oldSpecification}
                  </p>
                </div>
                <div className="flex-1 rounded-md border p-4">
                  <p className="revy-cjk text-xs leading-[1.5] text-muted-foreground">
                    {t('drbfm.table.newSpec')}
                  </p>
                  <p className="revy-cjk whitespace-pre-wrap text-sm leading-[1.6]">
                    {row.newSpecification}
                  </p>
                </div>
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default DesignChangeRow;
